/* eslint-disable @typescript-eslint/no-explicit-any */
import { Alert, Box, Card, CardContent, CardHeader, Chip, Divider, List, ListItem, ListItemText, Skeleton, SvgIcon, Typography } from '@mui/material';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { InventoryTask } from '../../../app/services/api/generated';
import { client, Order } from '../../../app/services/api/orderManagementClient';
import { ClipboardCheck, File06, ReceiptCheck, ShoppingCart01, SwitchHorizontal01 } from '../../../assets';
import CustomPagination from '../../../components/forms/Pagination';
import { Pill } from '../../../components/shared/Pill';
import { UserContext } from '../../../components/shared/useUser';
import { TaskType } from '../../../enums/TaskType';
import useNswagClient from '../../../hooks/api/useNswagClient';
import useLogError from '../../../hooks/useLogError';
import { theme } from '../../../theme';
import { formatDateString } from '../../../utils';

type DailyTasksProps = { storeNumber?: string };
const rowsPerPage = 5;

const now = new Date();

const endOfDay = new Date(now);
endOfDay.setHours(23, 59, 59, 999);

const startOfDay = new Date(now);
startOfDay.setHours(0, 0, 0, 0);

const DailyTasks: React.FC<DailyTasksProps> = ({ storeNumber }) => {
  const { t } = useTranslation('common');
  const { logError } = useLogError();
  const { getTasks } = useNswagClient();

  const [page, setPage] = useState(0);
  const [orderData, setOrderData] = useState<Order[]>([]);
  const [inventoryTasks, setInventoryTasks] = useState<InventoryTask[]>([]);
  const [loading, setLoading] = useState(false);
  const [isOrderError, setIsOrderError] = useState(false);

  const [isTaskError, setIsTaskError] = useState(false);

  const tasksData = useMemo(() => {
    const combined = [...orderData, ...inventoryTasks];

    // Sort tasks by date - most recent dates first
    return combined.sort((a, b) => {
      let dateA: Date | null = null;
      let dateB: Date | null = null;

      if ('cutOffDate' in a) {
        dateA = parseDDMMYYYY(a.cutOffDate ?? '');
      } else {
        dateA = new Date(a.startDate ?? '');
      }

      if ('cutOffDate' in b) {
        dateB = parseDDMMYYYY(b.cutOffDate ?? '');
      } else {
        dateB = new Date(b.startDate ?? '');
      }

      if (!dateA && !dateB) return 0;
      if (!dateA) return 1;
      if (!dateB) return -1;

      return dateB.getTime() - dateA.getTime();
    });
  }, [orderData, inventoryTasks]);

  const maxPages = useMemo(() => Math.ceil(tasksData.length / rowsPerPage), [tasksData]);

  const paginatedTasks = useMemo(
    () => tasksData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [page, tasksData],
  );

  useEffect(() => {
    setLoading(true);
    const filters = {
      orderNumber: '',
      item: '',
      orderType: -1,
      status: -1,
      deliveryDateFrom: new Date(Date.now() - 7 * 864e5).toISOString(),
      deliveryDateTo: new Date(Date.now() + 7 * 864e5).toISOString(),
      storeNumber,
      pageNumber: 1,
      pageSize: 1000000,
      sort: 'OrderNumber',
      direction: 'desc',
    };

    const promiseOrders = client.getOrders(filters);
    const promiseTasks = getTasks(storeNumber);

    Promise.allSettled([promiseOrders, promiseTasks])
      .then(([ordersResult, tasksResult]) => {
        if (ordersResult.status === 'fulfilled') {
          setOrderData(filterOrders(ordersResult.value.data.items));
        } else {
          logError('Error fetching orders: ' + ordersResult.reason);
          setIsOrderError(true);
        }

        if (tasksResult.status === 'fulfilled') {
          const tasks = tasksResult.value?.data?.tasks ?? [];
          setInventoryTasks(filterInventoryTasks(tasks));
        } else {
          logError('Error fetching tasks: ' + tasksResult.reason);
          setIsTaskError(true);
        }
      })
      .finally(() => setLoading(false));
  }, [storeNumber]);

  return (
    <Card sx={{ background: theme.palette.common.white, borderRadius: 2, boxShadow: 1 }}>
      <CardHeader
        sx={{ p: '24px', py: '16px' }}
        title={
          <Typography variant='textLG'
            fontWeight={700}
            color={theme.palette.custom.gray[900]}>
            {t('yourTasks')}
          </Typography>
        }
        subheader={tasksData.length > 0 && <Pill text={`${tasksData.length} ${tasksData.length === 1 ? t('taskRemaining') : t('tasksRemaining')}`} />}
        subheaderTypographyProps={{ display: 'inline-flex' }}
      />
      <CardContent
        sx={{
          p: 0,
          borderTop: `1px solid ${theme.palette.custom.gray[200]}`,
          borderBottom: `1px solid ${theme.palette.custom.gray[200]}`,
        }}
      >
        <CardContentInner loading={loading}
          isOrderError={isOrderError}
          isTaskError={isTaskError}
          paginatedTasks={paginatedTasks}
          maxPages={maxPages} />
      </CardContent>

      <CustomPagination
        useNoDataMessage={false}
        page={page}
        setPage={setPage}
        maxPages={maxPages}
        breakpointForChangeDisplay={120000}
      />

      {isOrderError && (
        <Alert sx={{ m: '12px' }}
          severity="warning">
          Error fetching orders, but other tasks are shown.
        </Alert>
      )}
      {isTaskError && (
        <Alert sx={{ m: '12px' }}
          severity="warning">
          Error fetching tasks, but orders are shown.
        </Alert>
      )}
    </Card>
  );
};

const parseDDMMYYYY = (dateStr: string): Date | null => {
  const [day, month, year] = dateStr.split('-').map(Number);
  if (!day || !month || !year) return null;
  return new Date(year, month - 1, day); // JS months are 0-indexed
};

const parseHHMM = (timeStr: string): { hours: number; minutes: number } | null => {
  const [hours, minutes] = timeStr.split(':').map(Number);
  if (hours == null || minutes == null) return null;
  return { hours, minutes };
};

const filterOrders = (orders: Order[]) => {
  return orders.filter((order) => {
    const cutOffDate = parseDDMMYYYY(order.cutOffDate ?? '');
    const cutOffTime = parseHHMM(order.cutOffTime ?? '');

    if (!cutOffDate) return false;

    // Combine date + time
    if (cutOffTime) {
      cutOffDate.setHours(cutOffTime.hours, cutOffTime.minutes, 0, 0);
    } else {
      cutOffDate.setHours(23, 59, 0, 0); // default to end of day
    }

    return cutOffDate > now && cutOffDate <= endOfDay && [0, 1].includes(order.statusId);
  });
};

const filterInventoryTasks = (tasks: InventoryTask[]) => {
  return tasks.filter((task) => new Date(task.startDate ?? '') <= endOfDay &&
    (task.percentageComplete ?? 0) < 100);
};

const CardContentInner = (props: { loading: boolean, isOrderError: boolean, isTaskError: boolean, paginatedTasks: (Order | InventoryTask)[], maxPages: number }) => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();

  const handleReviewClick = useCallback(
    (key: string | undefined, resourceId: number | undefined) => () => {
      const pathMap: Record<string, string> = {
        [TaskType.Transfer]: `/store/stock-transfer/${resourceId}`,
        [TaskType.StockCount]: `/store/perform-stock-count/${resourceId}`,
        [TaskType.PurchaseOrder]: `/create-po-receipt/${resourceId}`,
        [TaskType.Order]: `/store/order/${resourceId}`,
        [TaskType.TransportCost]: '/transport-cost',
      };
      if (key && pathMap[key]) navigate(pathMap[key]);
    },
    [navigate],
  );

  const isInventoryTask = (obj: unknown): obj is InventoryTask => {
    return typeof obj === 'object' && obj !== null && 'taskType' in obj;
  };

  if (props.loading) {
    return <Skeleton variant="rectangular"
      height={424} />;
  }

  if (props.isOrderError && props.isTaskError) {
    return <Alert sx={{ mx: '12px' }}
      severity="error">Error fetching tasks. Please try again.</Alert>;
  }

  if (props.paginatedTasks.length === 0) {
    return <Alert sx={{ mx: '12px' }}
      severity="info">{t('noAvailableTasks')}</Alert>;
  }

  return <List sx={{ width: '100%', p: 0 }}>
    {props.paginatedTasks.map((task, index) =>
      isInventoryTask(task) ? (
        <InventoryItem
          key={index}
          index={index}
          task={task}
          showSupplierName={true}
          handleReviewClick={handleReviewClick}
        />
      ) : (
        <OrderItem
          key={index}
          index={index}
          order={task}
          handleReviewClick={handleReviewClick}
        />
      ),
    )}

    {/* Add empty rows to maintain height */}
    {props.maxPages > 1 && Array.from({ length: Math.max(0, rowsPerPage - (props.paginatedTasks.length ?? 0)) }).map((_, index) => (
      <React.Fragment key={`item-${index}`}>
        <Divider variant="inset"
          component="li"
          sx={{
            borderBottom: `1px solid ${theme.palette.custom.gray[200]}`,
            ml: 0,
          }} />
        <ListItem key={`empty-row-${index}`}
          sx={{ height: '84px', backgroundColor: theme.palette.common.white }}>
        </ListItem>
      </React.Fragment>
    ))}
  </List>;
};

type ItemDisplayProps = {
  index: number;
  id: number;
  primaryText: string;
  secondaryText: React.ReactNode;
  handleReviewClick: (key: string | undefined, resourceId: number | undefined) => () => void;
  icon: JSX.Element;
  taskKey: string;
};

const ItemDisplay: React.FC<ItemDisplayProps> = ({
  index,
  id,
  primaryText,
  secondaryText,
  handleReviewClick,
  icon,
  taskKey,
}) => {
  const { t } = useTranslation('common');

  return <React.Fragment key={`item-${index}`}>
    {index != 0 && <Divider variant="inset"
      component="li"
      sx={{
        borderBottom: `1px solid ${theme.palette.custom.gray[200]}`,
        ml: 0,
      }} />}
    <ListItem sx={{ alignItems: 'flex-start center', px: '24px', height: '84px', cursor: 'pointer' }}
      onClick={handleReviewClick(taskKey, id)}>
      <div
        style={{
          width: '38px',
          height: '38px',
          border: `1px solid ${theme.palette.custom.gray[200]}`,
          borderRadius: '5px',
          marginRight: '16px',
          display: 'flex',
          justifyContent: 'center',
          alignSelf: 'center',
          backgroundColor: theme.palette.common.white,
          flexShrink: 0,
        }}
      >
        {icon}
      </div>
      <ListItemText
        sx={{
          mt: 0,
          mr: 5,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
        primary={
          <Typography variant="textSM"
            mt='0px'>
            {primaryText}
          </Typography>}
        secondary={secondaryText} />
      <Typography variant="textSM"
        fontWeight={600}
        textAlign={'center'}
        sx={{
          flexShrink: 0,
          '&:hover': {
            color: 'grey',
          },
        }}>
        {t('review')}
      </Typography>
    </ListItem>
  </React.Fragment>;
};

type OrderItemProps = {
  index: number;
  order: Order;
  handleReviewClick: (key: string | undefined, resourceId: number | undefined) => () => void;
};

const OrderItem: React.FC<OrderItemProps> = ({
  index,
  order,
  handleReviewClick,
}) => {
  const { selectedStore } = useContext(UserContext);
  const { t } = useTranslation('common');

  let primaryText;
  if (order.supplierName !== null && order.supplierName !== undefined) {
    primaryText = 'Order: ' + order.orderType + '-' + order.supplierName;
  }
  else {
    primaryText = 'Order: ' + order.orderType;
  }

  const icon = <SvgIcon sx={{ p: 0, height: '100%' }}><ShoppingCart01 /></SvgIcon>;

  let isOverdue = false;
  if ((parseDDMMYYYY(order.cutOffDate ?? '') ?? 0) < startOfDay) {
    isOverdue = true;
  }

  const subText = <>
    {<Box sx={{ mr: 2 }}>
      {t('due')}: {formatDateString(order.cutOffDate ?? '', selectedStore?.franchiseName)} {order.cutOffTime}
    </Box>
    }
    {isOverdue &&
      <Chip label={'Overdue'}
        size="small"
        sx={{ width: '120px', textAlign: 'center' }}
        color={'error'}
        variant='filled' />}
  </>;

  return <ItemDisplay
    index={index}
    id={order.id}
    primaryText={primaryText}
    secondaryText={subText}
    handleReviewClick={handleReviewClick}
    icon={icon}
    taskKey={TaskType.Order} />;
};

type InventoryItemProps = {
  showSupplierName: boolean;
  index: number;
  task: InventoryTask;
  handleReviewClick: (key: string | undefined, resourceId: number | undefined) => () => void;
};

const InventoryItem: React.FC<InventoryItemProps> = ({ showSupplierName, index, task, handleReviewClick }) => {
  const { selectedStore } = useContext(UserContext);
  const { t } = useTranslation('common');

  const getTaskIcon = (key: string | undefined) => {
    switch (key) {
    case TaskType.Transfer:
      return <SvgIcon sx={{ p: 0, height: '100%' }}><SwitchHorizontal01 /></SvgIcon>;
    case TaskType.StockCount:
      return <SvgIcon sx={{ p: 0, height: '100%' }}><ClipboardCheck /></SvgIcon>;
    case TaskType.PurchaseOrder:
      return <SvgIcon sx={{ p: 0, height: '100%' }}><ReceiptCheck /></SvgIcon>;
    default:
      return <SvgIcon sx={{ p: 0, height: '100%' }}><File06 /></SvgIcon>;
    }
  };
  let primaryText: string = '';
  if (task.taskType?.value == 'Purchase Order') {
    // The response of the Purchase Orders comes with the order number and order type, we don't need the order number so we used this to only get the order type of the task 
    const orderTypeMatch = task.description?.match(/Order Type:\s*([^,]*)/);
    const orderType = orderTypeMatch ? orderTypeMatch[1].trim() : '';
    // Extract the Order Vendor from the description
    const vendorMatch = task.description?.match(/Order Vendor:\s*(.*?)(,|$)/);
    const vendor = vendorMatch ? vendorMatch[1] : null;
    const orderIdMatch = task.description?.match(/Order ID:\s(\d+)/);
    const orderId = orderIdMatch && orderIdMatch[1] ? orderIdMatch[1] : '';

    // Format the primaryText as "Purchase Order: Receive <Order Type> - <Vendor>"
    const taskTypeValue = task.taskType?.value ?? '';
    if (showSupplierName && vendor !== null) {
      primaryText = `${taskTypeValue}: Receive ${orderType} - ${vendor}`;

    }
    else{
      primaryText = `${taskTypeValue}: Receive ${orderType}`;
    }
    if(orderId!= ''){
      primaryText = primaryText +' - '+ 'Order ID: '+ orderId;
    }
  }

  if (task.taskType?.value == 'Stock Transfer') {
    //We used this to only take the title and not display the date as on current IM system
    const formattedDescription = task.description?.replace(/^\d{2}\/\d{2}\/\d{4}\s*/, '');
    primaryText = `${task.taskType?.value}: ${formattedDescription}`;
  }
  if (task.taskType?.value == 'Stock Count' || task.taskType?.key == 'TransportCost') {
    primaryText = task.taskType?.value + ': ' + task.description;
  }

  let color: 'error' | 'warning' | 'info' | 'primary' | 'success';
  if (task.percentageComplete === 25) {
    color = 'warning';
  } else if (task.percentageComplete === 50) {
    color = 'info';
  } else if ((task.percentageComplete ?? 0) >= 75) {
    color = 'primary';
  } else {
    color = 'error';
  }
  const getNextSundayAtEndOfDay = (date: Date) => 
  {
    const result = new Date(date);
    const daysToAdd = 7;
    result.setDate(result.getDate() + daysToAdd);
    result.setHours(23, 59, 59, 999);
    return result;
  };

  let isOverdue = false;
  const dateNow = new Date();
  const startOfDay = new Date(dateNow.getFullYear(), dateNow.getMonth(), dateNow.getDate(), 0, 0, 0, 0);
  if ((task.taskType?.key == 'TransportCost' ? getNextSundayAtEndOfDay(new Date(task.startDate ?? '')) : new Date(task.startDate ?? '')) < startOfDay) {
    isOverdue = true;
  }

  const subText = <>
    {<Box sx={{ mr: 2 }}>
      {t('due')}: {
        formatDateString(
          task.taskType?.key == 'TransportCost'
            ? getNextSundayAtEndOfDay(new Date(task.startDate ?? ''))?.toString()
            : task.startDate ?? '',
          selectedStore?.franchiseName,
          true,
        )
      }   
    </Box>
    }
    {task.taskType?.key !== TaskType.Order && !isOverdue &&
      <Chip label={`${task.percentageComplete}% ${t('complete')}`}
        size="small"
        sx={{ width: '120px', textAlign: 'center' }}
        color={color}
        variant='outlined' />}
    {isOverdue &&
      <Chip label={'Overdue'}
        size="small"
        sx={{ width: '120px', textAlign: 'center' }}
        color={'error'}
        variant='filled' />}
  </>;
  const icon = getTaskIcon(task.taskType?.key);

  return <ItemDisplay
    index={index}
    id={task.resourceId ?? -1}
    primaryText={primaryText ?? ''}
    secondaryText={subText}
    handleReviewClick={handleReviewClick}
    icon={icon}
    taskKey={task.taskType?.key ?? ''}
  />;
};

export default DailyTasks;


